import React from 'react';

import Content from '@utils/Content';

import { useAddView } from '@hooks/useAddView';

import styles from './Single.module.scss';
import { Meta } from '@components/layout/Meta/Meta';
import Author from '@components/ui/Author/Author';
import MeetBlock from '@components/ui/MeetBlock/MeetBlock';
import TravelRecommendationBlock from '@components/ui/TravelRecommendationBlock/TravelRecommendationBlock';
import TravelstoryHeader from '@components/ui/TravelstoryHeader/TravelstoryHeader';

import { DestinationLinksProvider, FooterLinksProvider, SiteLinksProvider } from '@hooks/index';
import { GenericService, MailchimpService, PersonalService } from '@services/index';
import { FB_APP_ID, NavbarTabsTypes, PageProps, REVALIDATION_TIME } from '@constants/index';

import CookieDialog from '@components/forms/CookieDialog/CookieDialog';
import Layout from '@components/layout/Layout/Layout';
import { GetStaticPropsContext } from 'next';
import { TravelstoryModule } from 'modules/TravelstoryModule/TravelstoryModule.service';
import GraphqlService from '@services/GraphqlService';
import { ITravelstoryPublicationFields } from 'modules/TravelstoryModule/TravelstoryModule.props';

const { FacebookProvider } = require('react-facebook');

interface Props extends PageProps<ITravelstoryPublicationFields> {}

const TravelstoryPage = ({ footerLinks, destinationLinks, siteLinks, fields }: Props) => {
  useAddView(fields._id);

  const renderContent = () => {
    return fields.content?.map((item, index) => {
      return (
        // There is no other unique identifier unique to this content element
        <div key={index} className={styles[item.__typename]}>
          {Content.renderPreprElement(item, true)}
        </div>
      );
    });
  };

  return (
    <FacebookProvider appId={FB_APP_ID} xfbml>
      <FooterLinksProvider value={footerLinks}>
        <DestinationLinksProvider value={destinationLinks}>
          <SiteLinksProvider value={siteLinks}>
            <Layout paddings={false} selectedTab={NavbarTabsTypes.TRAVELSTORIES}>
              <div className={styles.TravelstoryPage}>
                <Meta
                  title={fields.meta_title || fields.title}
                  description={fields.meta_description || fields.header_subtitle}
                  image={fields.thumbnail?.[0]?.cdn_files[0]?.full}
                />
                <TravelstoryHeader
                  title={fields.title}
                  description={fields.header_subtitle}
                  backgroundImage={fields.thumbnail?.[0]?.cdn_files[0]?.full}
                  imageAuthor={fields.thumbnail ? fields.thumbnail[0].author : ''}
                />
                <div className={styles.InfoBlocks}>
                  <div className="container">
                    <div className="row">
                      {!!fields.about_corno_image &&
                        !!fields.about_corno_title &&
                        !!fields.about_corno_text && ( // i created a function for this, but typescript wouldn't agree
                          <div className="col-md-6">
                            <MeetBlock
                              title={fields.about_corno_title}
                              thumbnail={fields.about_corno_image?.[0]?.cdn_files[0]?.full}
                              description={fields.about_corno_text}
                              className={styles.InfoBlock}
                            />
                          </div>
                        )}

                      {!!fields.additional_information_text && (
                        <div className="col-md-6">
                          <TravelRecommendationBlock
                            title={fields.additional_information_title}
                            image={fields.additional_information_image?.[0]?.cdn_files[0]?.full}
                            content={fields.additional_information_text}
                            className={styles.InfoBlock}
                          />
                        </div>
                      )}
                    </div>
                    <div className="row">
                      <div className="col-lg-12">
                        <div
                          className={styles.Excerpt}
                          dangerouslySetInnerHTML={{ __html: fields.excerpt }}
                        />
                        {fields.author && (
                          <div className={styles.Author}>
                            <Author
                              image={fields.author[0].image[0].cdn_files[0]?.full}
                              author={fields.author[0].name}
                              url={`/${fields.about_corno_link[0]._slug}`}
                            />
                          </div>
                        )}
                        <div className={styles.Content}>{renderContent()}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Layout>
            <CookieDialog />
          </SiteLinksProvider>
        </DestinationLinksProvider>
      </FooterLinksProvider>
    </FacebookProvider>
  );
};

export default TravelstoryPage;

export const getStaticProps = async ({ params, locale, preview }: GetStaticPropsContext) => {
  if (!params) {
    return {
      notFound: true,
    };
  }

  const { travelstory } = params;

  GraphqlService.setLocale(locale);
  PersonalService.setLocale(locale);
  GenericService.setLocale(locale);
  MailchimpService.setLocale(locale);

  GraphqlService.setPreview(!!preview);

  const singlePage = await TravelstoryModule.getSinglePage(travelstory as string);

  if (!singlePage || !singlePage.fields) {
    return {
      notFound: true,
      revalidate: REVALIDATION_TIME,
    };
  }

  return {
    props: singlePage,
    revalidate: REVALIDATION_TIME,
  };
};

export const getStaticPaths = async () => {
  const paths = {
    paths: [],
    fallback: 'blocking',
  };

  return paths;
};
